import { createContext, useContext, useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const loadTicketDrawer = () => import('./TicketDrawer');
const TicketDrawer = lazyWithReload(loadTicketDrawer, 'TicketDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

// Fallback lúc Suspense tải chunk TicketDrawer (chunk lớn — TicketDetailContent 4k dòng).
// Trước đây fallback={null} → click "Chi tiết" xong KHÔNG thấy gì khi chunk đang tải → user
// tưởng hỏng, bấm lần 2 (đúng lúc chunk tải xong) → drawer mới hiện (bug "phải click 2 lần",
// report 2026-06-17). Bản vá riêng của ticket nay là BẢN CHUNG `common/DrawerLoadingFallback`
// (25.08.2026) — mọi drawer khác cũng mắc đúng bệnh đó, không có lý do để mỗi nơi một kiểu.

/**
 * URL-driven `?ticketId=<short>`.
 * API: useTicketDrawer() → { openTicket, closeTicket, version, bumpVersion, openTicketId }
 *
 * Prop `drawerWidth` — consumer truyền vào để override width của TicketDrawer.
 * Core 1500 (3-column layout), SaaS default 1100.
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openTicket: noProviderOpener('ticket'),
  closeTicket: () => {},
  version: 0,
  bumpVersion: () => {},
  openTicketId: null,
};

function TicketDrawerProviderOn({ children, drawerWidth }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId');
  const [version, setVersion] = useState(0);

  const openTicket = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('ticketId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('ticketId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeTicket = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('ticketId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('ticketId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('ticket', openTicket), [openTicket]);

  // Preload chunk TicketDrawer lúc trình duyệt rảnh → khi user bấm "Chi tiết" lần đầu,
  // chunk đã sẵn → drawer mở tức thì (không suspend). Bù cho chunk lớn của TicketDetailContent.
  useEffect(() => {
    const ric = typeof window !== 'undefined' && window.requestIdleCallback
      ? window.requestIdleCallback
      : (cb) => setTimeout(cb, 1500);
    const cancel = typeof window !== 'undefined' && window.cancelIdleCallback
      ? window.cancelIdleCallback
      : clearTimeout;
    const h = ric(() => { loadTicketDrawer(); });
    return () => cancel(h);
  }, []);

  return (
    <Ctx.Provider value={{ openTicket, closeTicket, version, bumpVersion, openTicketId: ticketId }}>
      {children}
      {ticketId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeTicket} width={drawerWidth} />}>
          <TicketDrawer open={!!ticketId} ticketId={ticketId} onClose={closeTicket} width={drawerWidth} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useTicketDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}

// Cụm Hỗ trợ tắt ở host (`modules.support = false` — Site Connect) ⇒ Provider rỗng: không đăng ký popup, không đọc
// `?ticketId=`, không dựng drawer (nội dung drawer gọi hàng chục /tickets/*). Xem lib/hostModules.
export const TicketDrawerProvider = providerIfModuleOn('support', TicketDrawerProviderOn);
